import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  archiveCode,
  clearExpiry,
  convertToDynamic,
  duplicateCode,
  editPayload,
  editRedirectTarget,
  getCode,
  lockCode,
  protectCode,
  removeProtection,
  setExpiry,
  type CreateCodeDesign,
  type CreateCodePayload,
  type QrCodeResponse,
} from "@qrody/api-client/codes";
import type { TranslatedError } from "@qrody/api-client/problem";
import { useTranslations } from "@/i18n/IntlProvider";
import {
  QrGenerator,
  buildSaveInput,
  resolveCapabilities,
  type GeneratorPlanId,
  type QrGeneratorState,
} from "@qrody/qr-generator";
import { Save } from "lucide-react";
import { ErrorAlert, type BannerError } from "./ErrorAlert";
import { CodeSidebarPanel } from "./CodeSidebarPanel";
import { CodeDownloadDropdown } from "./CodeDownloadDropdown";
import { CodeProtectionForm } from "./CodeProtectionForm";
import { CodeExpiryForm } from "./CodeExpiryForm";
import { CodeHistoryCard } from "./CodeHistoryCard";
import { RecentScansCard } from "./RecentScansCard";
import { ScanTimeseriesCard } from "./ScanTimeseriesCard";
import {
  seedFromCode,
  previewPayloadOverrideForCode,
} from "./code-utils";

/**
 * Orchestrator for the /codes/$id page.
 *
 * Loads the code via TanStack-Query, fans out mutations for every
 * lifecycle action (lock / convert / duplicate / archive / protect /
 * expire) plus payload + redirect-target edits, and lays out:
 *
 *   - the sidebar slot (identity + lifecycle CTAs + mode toggle), and
 *   - the main content (payload editor + auto-fit cards for downloads,
 *     protection and expiry).
 *
 * Optimistic updates: payload + redirect-target edits write the next
 * code into the cache before the PATCH lands so the editor stays
 * stable. On error the cache rolls back to the pre-mutation snapshot.
 *
 * `useCurrentCapabilitiesSafe`-style try/catch from the legacy panel is
 * gone — the SPA-side BFF flow has no oidc-spa Mount race, so the
 * defensive net is dead code under ADR-027.
 */
type SavePayloadVariables = {
  payload: CreateCodePayload;
  design: CreateCodeDesign;
};

export function CodeDetailPanel({ id }: { id: string }) {
  const t = useTranslations("app.codes.detail");
  const navigate = useNavigate();
  const { locale } = useParams({ strict: false }) as { locale?: string };
  const lang = locale ?? "de";
  const qc = useQueryClient();
  const queryKey = ["code", id] as const;

  const codeQuery = useQuery<QrCodeResponse, TranslatedError>({
    queryKey,
    queryFn: async () => {
      const res = await getCode(id);
      if (!res.ok) throw res.error;
      return res.value;
    },
    retry: false,
  });

  // Plan-aware editor capabilities. While the query is in flight we
  // conservatively render the logged-in-free branch — that matches the
  // visual a free user sees on response and avoids flashing the locks
  // off for Pro users (none of the unlock flags flip until the data
  // lands). Same `["billing-plan"]` key as the BillingRoute so the two
  // surfaces share cache.
  const planQuery = useQuery({
    queryKey: ["billing-plan"],
    queryFn: () => api<{ planId: string }>("/v1/billing/plan"),
    retry: false,
    staleTime: 60_000,
  });
  const planId = coercePlanId(planQuery.data?.planId);
  const caps = useMemo(
    () => resolveCapabilities({ isLoggedIn: true, planId }),
    [planId],
  );

  const invalidateLists = () => {
    void qc.invalidateQueries({ queryKey: ["codes"] });
  };

  // PATCH payload (DRAFT) — optimistic to keep the form stable.
  // Saves both the payload AND the editor's current design (foreground,
  // background, frame, ecc, minVersion). Earlier this used `code.design`
  // off the cached server response, which silently discarded every edit
  // the user made in the Gestaltung panel — colors/frame/ECC looked
  // like they saved but the PATCH carried the previous values.
  const editPayloadMut = useMutation<
    QrCodeResponse,
    TranslatedError,
    SavePayloadVariables,
    { previous?: QrCodeResponse }
  >({
    mutationFn: async ({ payload, design }) => {
      const res = await editPayload(id, payload, design);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<QrCodeResponse>(queryKey);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
    onSuccess: (next) => {
      qc.setQueryData(queryKey, next);
      invalidateLists();
    },
  });

  // PATCH redirect-target (LOCKED + DYNAMIC).
  const editRedirectMut = useMutation<
    QrCodeResponse,
    TranslatedError,
    CreateCodePayload,
    { previous?: QrCodeResponse }
  >({
    mutationFn: async (payload) => {
      const res = await editRedirectTarget(id, payload);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<QrCodeResponse>(queryKey);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
    onSuccess: (next) => {
      qc.setQueryData(queryKey, next);
      invalidateLists();
    },
  });

  const lockMut = useMutation<QrCodeResponse, TranslatedError, void>({
    mutationFn: async () => {
      const res = await lockCode(id);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onSuccess: (next) => {
      qc.setQueryData(queryKey, next);
      invalidateLists();
    },
  });

  const convertMut = useMutation<QrCodeResponse, TranslatedError, void>({
    mutationFn: async () => {
      const res = await convertToDynamic(id);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onSuccess: (next) => {
      qc.setQueryData(queryKey, next);
      invalidateLists();
    },
  });

  const duplicateMut = useMutation<QrCodeResponse, TranslatedError, void>({
    mutationFn: async () => {
      const res = await duplicateCode(id);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onSuccess: (created) => {
      invalidateLists();
      void navigate({
        to: "/$locale/codes/$id" as never,
        params: { locale: lang, id: created.id } as never,
      });
    },
  });

  const archiveMut = useMutation<void, TranslatedError, void>({
    mutationFn: async () => {
      const res = await archiveCode(id);
      if (!res.ok) throw res.error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
      invalidateLists();
    },
  });

  const protectMut = useMutation<QrCodeResponse, TranslatedError, string>({
    mutationFn: async (password) => {
      const res = await protectCode(id, password);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onSuccess: (next) => qc.setQueryData(queryKey, next),
  });

  const removeProtectionMut = useMutation<QrCodeResponse, TranslatedError, void>({
    mutationFn: async () => {
      const res = await removeProtection(id);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onSuccess: (next) => qc.setQueryData(queryKey, next),
  });

  const setExpiryMut = useMutation<QrCodeResponse, TranslatedError, string>({
    mutationFn: async (iso) => {
      const res = await setExpiry(id, iso);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onSuccess: (next) => qc.setQueryData(queryKey, next),
  });

  const clearExpiryMut = useMutation<QrCodeResponse, TranslatedError, void>({
    mutationFn: async () => {
      const res = await clearExpiry(id);
      if (!res.ok) throw res.error;
      return res.value;
    },
    onSuccess: (next) => qc.setQueryData(queryKey, next),
  });

  const [saveError, setSaveError] = useState<BannerError | null>(null);
  const [editorState, setEditorState] = useState<QrGeneratorState | null>(null);

  // Sidebar callbacks need stable identity — CodeSidebarPanel feeds
  // them into a useMemo that controls the slot, and a new closure on
  // every render here would invalidate the memo every render → infinite
  // setNode loop in useSetAppSidebarSlot.
  const handleLock = useCallback(() => {
    if (!confirm(t("lifecycle.lockConfirm"))) return;
    lockMut.mutate();
  }, [lockMut, t]);
  const handleConvert = useCallback(() => {
    if (!confirm(t("mode.convertConfirm"))) return;
    convertMut.mutate();
  }, [convertMut, t]);
  const handleDuplicate = useCallback(() => {
    duplicateMut.mutate();
  }, [duplicateMut]);
  const handleArchive = useCallback(() => {
    if (!confirm(t("lifecycle.archiveConfirm"))) return;
    archiveMut.mutate();
  }, [archiveMut, t]);

  if (codeQuery.isPending) {
    return (
      <div className="rounded-md border bg-muted/30 p-6 text-sm text-muted-foreground">
        {t("loading")}
      </div>
    );
  }
  if (codeQuery.error) {
    return (
      <ErrorAlert error={codeQuery.error} onRetry={() => codeQuery.refetch()} />
    );
  }
  const code = codeQuery.data;
  const isArchived = Boolean(code.archivedAt);
  const isStatic = code.mode === "STATIC";
  const isLocked = code.locked;

  const onSave = (next: CreateCodePayload, design: CreateCodeDesign) => {
    setSaveError(null);
    // ADR-025: redirect-target edit on LOCKED+DYNAMIC stays compatible
    // with URL / FILE only; the editor enforces this upstream via the
    // redirect-only branch, so we never PATCH a non-URL payload here.
    // The redirect-target endpoint does NOT accept a design block (the
    // matrix is locked), so design changes are dropped on that branch
    // — colors/frame/ECC stay editable in DRAFT and are silently
    // ignored once locked, mirroring the backend contract.
    if (isLocked && !isArchived && code.mode === "DYNAMIC") {
      editRedirectMut.mutate(next, {
        onError: (err) => setSaveError(err),
      });
      return;
    }
    editPayloadMut.mutate(
      { payload: next, design },
      {
        onError: (err) => setSaveError(err),
      },
    );
  };

  const saving = editPayloadMut.isPending || editRedirectMut.isPending;
  const protectBusy =
    protectMut.isPending || removeProtectionMut.isPending;
  const expiryBusy = setExpiryMut.isPending || clearExpiryMut.isPending;

  return (
    <div className="space-y-6">
      <CodeSidebarPanel
        code={code}
        busy={{
          lock: lockMut.isPending,
          convert: convertMut.isPending,
          duplicate: duplicateMut.isPending,
          archive: archiveMut.isPending,
        }}
        errors={{
          lock: lockMut.error ?? null,
          convert: convertMut.error ?? null,
          duplicate: duplicateMut.error ?? null,
          archive: archiveMut.error ?? null,
        }}
        onLock={handleLock}
        onConvert={handleConvert}
        onDuplicate={handleDuplicate}
        onArchive={handleArchive}
      />

      {saveError ? (
        <ErrorAlert error={saveError} onRetry={() => setSaveError(null)} />
      ) : null}

      {/* Editor — full QrGenerator embed, exactly like the legacy
          web/.../code-detail-panel.tsx page. Flags lifted verbatim:
          - disableTypeChange  : the type is baked into the code-id
          - disablePayloadFields : true for STATIC+locked and archived
                                   codes (the matrix cannot move).
                                   DYNAMIC+locked keeps the redirect-
                                   target field editable (handled
                                   inside the generator).
          - disableEcc         : locked OR archived; the ECC byte is
                                   part of the matrix layout.
          - previewPayloadOverride : DYNAMIC codes encode the slug-
                                   redirect URL, not the edit-time
                                   target. */}
      <section className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base font-semibold">{t("editor.title")}</h2>
          {!isArchived && editorState && editorState.payload != null ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
              disabled={saving}
              onClick={() => {
                if (editorState.payload == null) return;
                const result = buildSaveInput({
                  payload: editorState.payload,
                  design: editorState.design,
                });
                if (!result.ok) return;
                onSave(result.value.payload, result.value.design);
              }}
            >
              <Save className="size-4" aria-hidden />
              {saving ? t("editor.saving") : t("editor.save")}
            </button>
          ) : null}
        </div>
        <QrGenerator
          caps={caps}
          seed={seedFromCode(code)}
          disableTypeChange
          disablePayloadFields={
            (isLocked && isStatic) || isArchived
          }
          disableEcc={isLocked || isArchived}
          previewPayloadOverride={previewPayloadOverrideForCode(code)}
          onStateChange={setEditorState}
          hideInlineActions
        />
      </section>

      {/* Auto-fit cards — `min(280px, 100%)` keeps the cards from
          forcing horizontal scroll on narrow viewports, while
          `auto-fit + 1fr` fills every free track without breakpoint
          jumps. Lifted from web/.../code-detail-panel.tsx so the
          visual contract carries over verbatim. */}
      <div className="grid auto-rows-fr gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(280px,100%),1fr))]">
        <section className="space-y-2 rounded-lg border bg-card p-4">
          <h2 className="text-sm font-semibold">{t("downloads.title")}</h2>
          <p className="text-xs text-muted-foreground">{t("downloads.desc")}</p>
          <CodeDownloadDropdown id={code.id} />
        </section>

        {/* Password + expiry only on DYNAMIC: STATIC carries the payload
            in the matrix, the backend can't enforce either on scan.
            Backend would 422 anyway; this gate spares the frustration. */}
        {!isStatic && !isArchived ? (
          <section className="space-y-2 rounded-lg border bg-card p-4">
            <h2 className="text-sm font-semibold">{t("protection.title")}</h2>
            <p className="text-xs text-muted-foreground">{t("protection.desc")}</p>
            <CodeProtectionForm
              code={code}
              busy={protectBusy}
              onSet={(p) => protectMut.mutate(p)}
              onRemove={() => removeProtectionMut.mutate()}
            />
            {protectMut.error ? <ErrorAlert error={protectMut.error} /> : null}
            {removeProtectionMut.error ? (
              <ErrorAlert error={removeProtectionMut.error} />
            ) : null}
          </section>
        ) : null}

        {!isStatic && !isArchived ? (
          <section className="space-y-2 rounded-lg border bg-card p-4">
            <h2 className="text-sm font-semibold">{t("expiry.title")}</h2>
            <p className="text-xs text-muted-foreground">{t("expiry.desc")}</p>
            <CodeExpiryForm
              code={code}
              busy={expiryBusy}
              onSet={(iso) => setExpiryMut.mutate(iso)}
              onClear={() => clearExpiryMut.mutate()}
            />
            {setExpiryMut.error ? <ErrorAlert error={setExpiryMut.error} /> : null}
            {clearExpiryMut.error ? (
              <ErrorAlert error={clearExpiryMut.error} />
            ) : null}
          </section>
        ) : null}
      </div>

      {/* Scan time-series — full-width card below the auto-fit grid.
          Only renders meaningfully for DYNAMIC codes; STATIC ones show
          a hint that explains why there are no recorded scans. */}
      {!isArchived ? (
        <ScanTimeseriesCard codeId={code.id} mode={code.mode} />
      ) : null}
      {!isArchived && code.mode === "DYNAMIC" ? (
        <RecentScansCard codeId={code.id} mode={code.mode} />
      ) : null}
      <CodeHistoryCard codeId={code.id} />
    </div>
  );
}

/** Narrow the loose `planId` string from `/v1/billing/plan` to the
 *  generator's `GeneratorPlanId` union; unknown values fall back to
 *  `null` so `resolveCapabilities` renders the conservative branch. */
function coercePlanId(value: string | undefined): GeneratorPlanId | null {
  if (value === "tier1_free" || value === "pro_quarterly" || value === "pro_yearly") {
    return value;
  }
  return null;
}
